/**
 * AB#21698 "No Fill": fixed logos the customer hid on their uploaded design.
 *
 * The customizer stores the choice on the ordered product, on the logo's entry in
 * `fixed_logo_overrides` (keyed "style|side|placement title|file"), as `no_fill_design_id` - the
 * uploaded design it was made on. Production must leave such a logo out, so the order page lists
 * it. Only entries made on the ordered design count; one made on another design is ignored.
 *
 * Mirrors `FixedLogoNoFill::hiddenLogos()` in the backend, which feeds the product Excel sheet.
 */

export type HiddenFixedLogo = { title: string; side: string };

type ProductLike = {
  design_id?: number | string | null;
  fixed_logo_overrides?: unknown;
};

function asObject(value: unknown): Record<string, unknown> | null {
  let parsed = value;
  // Polymorphic JSON: an object, PHP's `[]` for an empty map, or a double-encoded string.
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : null;
}

export function hiddenFixedLogos(product: ProductLike | null | undefined): HiddenFixedLogo[] {
  const overrides = asObject(product?.fixed_logo_overrides);
  const designId = Number(product?.design_id);
  if (!overrides || !Number.isFinite(designId) || designId <= 0) return [];

  const out: HiddenFixedLogo[] = [];
  const seen = new Set<string>();
  for (const [key, entry] of Object.entries(overrides)) {
    const noFill = Number((entry as { no_fill_design_id?: unknown } | null)?.no_fill_design_id);
    if (!Number.isFinite(noFill) || noFill !== designId) continue;
    const [, side = '', title = ''] = key.split('|');
    const label = `${title.trim()}|${side.trim()}`;
    if (!title.trim() || seen.has(label)) continue;
    seen.add(label);
    out.push({ title: title.trim(), side: side.trim() });
  }
  return out;
}
